import { generatedApi } from './api.generated';
import { createEndpointExports, createHookExports, LIST_TAG_ID } from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';
import { DEX_VAULT_DEPLOYER_API_ENDPOINTS as cfg } from './config';
import { dexVaultsApi } from '@skynet/data-dex-vaults';

const enhancedDexVaultDeployerApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['DexDeployer'],
  endpoints: {
    [cfg.getDeployer.generated]: {
      providesTags: (_, __, arg) => [
        { type: 'DexDeployer', id: `${arg.blockchainId}-${arg.deployerId}` },
        { type: 'DexDeployer', id: arg.deployerId },
      ],
    },
    [cfg.findDeployer.generated]: {
      providesTags: (_, __, arg) => [
        { type: 'DexDeployer', id: `${arg.blockchainId}-${arg.address}` },
      ],
    },
    [cfg.addDeployer.generated]: {
      invalidatesTags: ['DexDeployer'],
    },
    [cfg.deployVault.generated]: {
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Deploying a vault changes that vault's deployment state (its detail)
          // and its row in the list, so invalidate the per-vault tag plus the
          // collection LIST tag on `dexVaultsApi` (tag type renamed to `Vaults`).
          dispatch(
            dexVaultsApi.util.invalidateTags([
              { type: 'Vaults', id: arg.vaultId },
              { type: 'Vaults', id: LIST_TAG_ID },
            ]),
          );
        } catch (error) {
          console.error('Deploy vault error:', error);
        }
      },
    },
    [cfg.withdrawGas.generated]: {
      invalidatesTags: (result, _, arg) => {
        const deployerId = result?.data?.deployerId ?? arg.withdrawGasRequest.deployerId;
        const blockchainId = result?.data?.blockchainId ?? arg.withdrawGasRequest.blockchainId;
        if (deployerId != null && blockchainId != null) {
          return [{ type: 'DexDeployer', id: `${blockchainId}-${deployerId}` }];
        }
        return [];
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;

          // Withdrawing gas is not scoped to a single vault, so invalidate every
          // `Vaults`-tagged entry (a type-only tag matches all of its ids).
          dispatch(dexVaultsApi.util.invalidateTags([{ type: 'Vaults' }]));
        } catch {
          // swallow — invalidatesTags already handles DexDeployer cache cleanup
        }
      },
    },
  },
});

export const dexVaultDeployerApi: typeof enhancedDexVaultDeployerApi = enhancedDexVaultDeployerApi;

export const hookExports: HookExports<typeof cfg, typeof dexVaultDeployerApi> = createHookExports(
  cfg,
  dexVaultDeployerApi,
);

const endpointExports = createEndpointExports(cfg, dexVaultDeployerApi);

export const dexVaultDeployerApiEndpoints: typeof endpointExports = endpointExports;
